import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Lock, Eye, EyeOff, ArrowLeft, Package, Globe, Zap } from 'lucide-react';
import AppLogoFull from '../components/AppLogoFull';
import LanguageSelector from '../components/LanguageSelector';
import { formatErrorMessage } from '../utils/errorHandler';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroPanel = ({ t, navigate }) => (
    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80&auto=format"
            alt="Logistics"
            className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <button
            onClick={() => navigate('/')}
            className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-colors"
        >
            <ArrowLeft size={16} />
            {t('home')}
        </button>
        <div className="relative z-10 flex flex-col justify-end p-6 lg:p-10 w-full">
            <h2 className="text-2xl lg:text-3xl font-bold text-white leading-tight">
                {t('loginHeroTitle') || 'Simplifiez la gestion de votre fret'}
            </h2>
            <p className="mt-3 text-white/80 text-base max-w-md leading-relaxed">
                {t('loginAIDesc')}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
                    <Package size={16} className="text-white/90 shrink-0" />
                    <span className="text-sm text-white/90">{t('loginFeatureTracking')}</span>
                </div>
                <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
                    <Globe size={16} className="text-white/90 shrink-0" />
                    <span className="text-sm text-white/90">{t('loginFeatureMultiCountry')}</span>
                </div>
                <div className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-2">
                    <Zap size={16} className="text-white/90 shrink-0" />
                    <span className="text-sm text-white/90">{t('loginFeatureNotifications')}</span>
                </div>
            </div>
        </div>
    </div>
);

const ResetPassword = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!token) setError(t('resetTokenMissing'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.newPassword !== formData.confirmPassword) {
            setError(t('passwordsMismatch'));
            return;
        }
        if (formData.newPassword.length < 6) {
            setError(t('passwordMinLength'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: formData.newPassword }),
            });
            const data = await response.json();
            if (response.ok) {
                navigate('/login?reset=success');
            } else {
                setError(formatErrorMessage(data));
            }
        } catch (err) {
            setError(formatErrorMessage(err));
        }
        setLoading(false);
    };

    const passwordsMismatch =
        formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword;

    return (
        <div className="min-h-screen flex bg-[hsl(var(--background))]">
            <SEOHead title={t('newPasswordTitle')} description={t('newPasswordDesc')} noindex />
            <HeroPanel t={t} navigate={navigate} />

            {/* Right panel */}
            <div className="flex-1 flex items-center justify-center p-4 relative">
                <div className="absolute top-4 right-4">
                    <LanguageSelector />
                </div>

                <Card className="w-full max-w-md border border-[hsl(var(--border))]">
                    <CardHeader className="space-y-1 flex flex-col items-center">
                        <AppLogoFull height={40} className="mb-4" />
                        <CardTitle className="text-xl font-bold text-center text-[hsl(var(--foreground))]">
                            {t('resetPasswordCardTitle')}
                        </CardTitle>
                        <CardDescription className="text-center">
                            {t('chooseStrongPassword')}
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        {!token ? (
                            <>
                                <Alert variant="destructive">
                                    <AlertDescription>{t('invalidResetLink')}</AlertDescription>
                                </Alert>
                                <Link
                                    to="/forgot-password"
                                    className="flex items-center justify-center text-accent hover:text-accent/80 text-sm font-medium"
                                >
                                    {t('requestNewLink')}
                                </Link>
                            </>
                        ) : (
                            <>
                                {error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="newPassword">{t('newPassword')}</Label>
                                        <div className="relative">
                                            <Input
                                                id="newPassword"
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.newPassword}
                                                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                                placeholder={t('enterNewPassword')}
                                                required
                                                minLength={6}
                                                className="pr-10"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                tabIndex={-1}
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                                        <div className="relative">
                                            <Input
                                                id="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={formData.confirmPassword}
                                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                                placeholder={t('confirmYourPassword')}
                                                required
                                                minLength={6}
                                                className="pr-10"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                                tabIndex={-1}
                                            >
                                                {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                            </button>
                                        </div>
                                    </div>

                                    {passwordsMismatch && (
                                        <p className="text-sm text-red-600">{t('passwordsDontMatch')}</p>
                                    )}

                                    <Button
                                        type="submit"
                                        className="w-full bg-accent hover:bg-accent/90"
                                        disabled={loading || !formData.newPassword || !formData.confirmPassword || passwordsMismatch}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                                {t('resetting')}
                                            </>
                                        ) : (
                                            <>
                                                <Lock className="h-4 w-4 mr-2" />
                                                {t('setNewPassword')}
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </>
                        )}

                        <div className="pt-2">
                            <Link
                                to="/login"
                                className="flex items-center justify-center text-accent hover:text-accent/80 text-sm font-medium"
                            >
                                <ArrowLeft className="h-4 w-4 mr-1" />
                                {t('backToLogin')}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPassword;
